import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { createInfo } from '@liveauctioneers/hammer-ui-core/card/components/createInfo';
import {
    createSaveSearchKeyBySearchParams,
    deleteSavedSearch,
    getIsSavedSearch,
    submitSaveSearch,
} from '@/redux/modules/saveSearch';
import { DEFAULT_STATUS_FOR_ONLINE_SEARCH } from '@/utils/queryParams';
import { DotsThreeOutlineVertical } from '@liveauctioneers/hammer-ui-core/icons';
import { getCatalogRegistration } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import {
    getItemSummaryBidCount,
    getItemSummaryBidderHasBid,
    getItemSummaryCatalogId,
    getItemSummaryCatalogStatus,
    getItemSummaryIsTimedPlusAuction,
    getItemSummarySellerId,
    getItemSummaryTitle,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getSearchUrlWithParams } from '@/utils/urls';
import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import {
    ItemCardMenuClickTrigger,
    ItemCardNavigationTrigger,
    useTrackItemCardMenuClickAnalytics,
    useTrackRegisterForAuctionPressedAnalytics,
} from '@/utils/amplitude/analytics';
import { ItemSummary } from '@/types/item/ItemSummary';
import { Menu, MenuItem } from '@liveauctioneers/hammer-ui-core/menu';
import { openExcludeHouseModal, openRetractBidModal } from '@/redux/modules/modal';
import { toast } from '@liveauctioneers/hammer-ui-core/toast';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCanRetractTimedPlusAuctionBid } from '@/hooks/timedPlus/retractBid/useCanRetractTimedPlusAuctionBid';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useItemCardNavigation } from '@/utils/amplitude/experiments/hooks/useItemCardNavigation';
import { useNavigate } from 'react-router-dom';
import { useWindowIsMobile } from '@liveauctioneers/hammer-ui-core/providers';
import ItemCardPriceRow from '@/components/GridItemCard/Card/components/ItemCardPriceRow/ItemCardPriceRow';
import RegisterLinkOrPrice from '@/components/GridItemCard/Card/CardOptions/TimedPlusItemCard/components/RegisterLinkOrPrice/RegisterLinkOrPrice';

export const PriceAndMenuComponent = createInfo<ItemSummary & { position: number }>({
    className: 'line-clamp-0',
    useText: ({ itemId, position, promoted }) => {
        const navigate = useNavigate();
        const dispatch = useAppDispatch();

        const bidCount = useAppSelector((state) => getItemSummaryBidCount(state, itemId));
        const bidderHasBid = useAppSelector((state) => getItemSummaryBidderHasBid(state, itemId));
        const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
        const catalogStatus = useAppSelector((state) => getItemSummaryCatalogStatus(state, itemId));
        const isTimedPlusAuction = useAppSelector((state) => getItemSummaryIsTimedPlusAuction(state, itemId));
        const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
        const title = useAppSelector((state) => getItemSummaryTitle(state, itemId));

        const [linkHover, setLinkHover] = useState(false);
        const { formatMessage } = useIntl();
        const [isMenuOpen, setIsMenuOpen] = useState(false);
        const isMobile = useWindowIsMobile();
        const trackItemCardMenuClickAnalytics = useTrackItemCardMenuClickAnalytics();
        const { onNavigateInitiated } = useItemCardNavigation(itemId);
        const trackRegisterForAuctionPressedAnalytics = useTrackRegisterForAuctionPressedAnalytics();

        const saveSearchParams = useMemo(() => {
            return { keyword: title, status: DEFAULT_STATUS_FOR_ONLINE_SEARCH };
        }, [title]);

        const isLoggedIn = useAppSelector(isUserLoggedIn);
        const saveSearchKey = createSaveSearchKeyBySearchParams(saveSearchParams);
        const isSavedSearch = useAppSelector((state) => getIsSavedSearch(state, saveSearchKey));
        const enterLinkHoverState = useCallback(() => {
            setLinkHover(true);
        }, []);

        const leaveLinkHoverState = useCallback(() => {
            setLinkHover(false);
        }, []);

        const onSeeSimilarItemsClicked = useCallback(() => {
            const itemSearchKeyword = saveSearchParams?.keyword;
            trackItemCardMenuClickAnalytics({
                houseId: sellerId,
                itemId,
                trigger: ItemCardMenuClickTrigger.SeeSimilarItems,
            });
            navigate(
                getSearchUrlWithParams({
                    keyword: itemSearchKeyword,
                    page: 1,
                    status: 'online',
                })
            );
        }, [saveSearchParams?.keyword, trackItemCardMenuClickAnalytics, sellerId, itemId, navigate]);

        const toggleSearchAlert = useCallback(() => {
            if (isSavedSearch) {
                dispatch(deleteSavedSearch(saveSearchParams));
                toast('success', {
                    description: formatMessage({ id: 'searchRemoveAlertToast' }),
                    title: formatMessage({ id: 'toastMessages.titles.success' }),
                });
            } else {
                dispatch(submitSaveSearch(saveSearchParams, true));
                toast('success', {
                    description: formatMessage({ id: 'searchCreateAlertToast' }),
                    title: formatMessage({ id: 'toastMessages.titles.success' }),
                });
            }
        }, [dispatch, formatMessage, isSavedSearch, saveSearchParams]);

        const showConfirmExcludeModal = useCallback(() => {
            trackItemCardMenuClickAnalytics({
                houseId: sellerId,
                itemId,
                trigger: ItemCardMenuClickTrigger.ExcludeHouseFromSearch,
            });
            dispatch(openExcludeHouseModal(sellerId));
        }, [dispatch, sellerId, trackItemCardMenuClickAnalytics, itemId]);

        // LOGIC FOR RETRACTING BID
        const onRetractBidClicked = useCallback(() => {
            trackItemCardMenuClickAnalytics({
                houseId: sellerId,
                itemId,
                trigger: ItemCardMenuClickTrigger.RetractBid,
            });
            dispatch(openRetractBidModal(itemId));
        }, [dispatch, itemId, trackItemCardMenuClickAnalytics, sellerId]);

        const [canRetractBid, setCanRetractBid] = useState(false);
        const canRetractTimedPlusAuctionBid = useCanRetractTimedPlusAuctionBid(itemId);
        const { approved, registered } = useAppSelector((state) => getCatalogRegistration(state, catalogId));

        useEffect(() => {
            const hasBid = (approved || registered) && bidderHasBid;
            const canRetractBasedOnCatalogType = isTimedPlusAuction
                ? canRetractTimedPlusAuctionBid
                : catalogStatus !== 'live';
            setCanRetractBid(hasBid && canRetractBasedOnCatalogType);
        }, [approved, bidderHasBid, canRetractTimedPlusAuctionBid, catalogStatus, isTimedPlusAuction, registered]);

        return (
            <div className="relative flex items-center justify-between">
                {isTimedPlusAuction ? (
                    <RegisterLinkOrPrice
                        itemId={itemId}
                        linkHover={linkHover}
                        onNavigate={() => {
                            onNavigateInitiated(ItemCardNavigationTrigger.PriceClick, { position, promoted });
                        }}
                        onRegisterForAuctionClicked={() => trackRegisterForAuctionPressedAnalytics()}
                    />
                ) : (
                    <ItemCardPriceRow
                        isHovered={linkHover}
                        itemId={itemId}
                        onMouseEnter={enterLinkHoverState}
                        onMouseLeave={leaveLinkHoverState}
                        onNavigate={() => {
                            onNavigateInitiated(ItemCardNavigationTrigger.PriceClick, { position, promoted });
                        }}
                        showBidCount={Boolean(bidCount)}
                    />
                )}
                {isLoggedIn && (
                    <div className="absolute right-0 mt-2xs">
                        <Menu
                            hasRoundedBorders
                            menuSize="md"
                            numberOfColumns={1}
                            onMenuClose={() => setIsMenuOpen(false)}
                            onMenuOpen={() => setIsMenuOpen(true)}
                            trigger={
                                <IconButton
                                    aria-label={`Menu options for ${title}`}
                                    className={cn(
                                        'text-text-inverse group-hover:text-surface-500 xsMax:text-surface-500',
                                        {
                                            'text-surface-500': isMenuOpen || isMobile,
                                        }
                                    )}
                                    hideTooltip
                                    icon={DotsThreeOutlineVertical}
                                    iconStyle="fill"
                                    size="sm"
                                    variant="transparent"
                                />
                            }
                        >
                            <MenuItem
                                onClick={onSeeSimilarItemsClicked}
                                primaryLabel={formatMessage({ id: 'search_see_similar' })}
                            />
                            <MenuItem
                                onClick={toggleSearchAlert}
                                primaryLabel={formatMessage({
                                    id: isSavedSearch ? 'search_remove_alert' : 'search_create_alert',
                                })}
                            />
                            <MenuItem
                                onClick={showConfirmExcludeModal}
                                primaryLabel={formatMessage({
                                    id: 'search_exclude_this_auction_house',
                                })}
                            />
                            {canRetractBid && (
                                <MenuItem
                                    onClick={onRetractBidClicked}
                                    primaryLabel={formatMessage({
                                        id: 'retract_bid',
                                    })}
                                />
                            )}
                        </Menu>
                    </div>
                )}
            </div>
        );
    },
});
